import { context, SpanKind, trace, Tracer } from "@opentelemetry/api";

import { InteractionConfig, RootSpan } from "../types";
import { InteractionEvent } from "./types";
import {
  calculateScrollDepth,
  emitInteractionLog,
  extractElementInfo,
  isSafeToTrackKey,
  isSensitiveInput,
  setInteractionAttributes,
} from "./utils";

type ListenerRegistration = {
  type: keyof DocumentEventMap;
  listener: EventListener;
  options?: boolean | AddEventListenerOptions;
};

type ListenerDefinition = {
  enabled?: boolean;
  type: keyof DocumentEventMap;
  guard: string;
  handler: (event: Event) => void;
  options?: boolean | AddEventListenerOptions;
};

export class UserInteractionInstrumentation {
  private tracer: Tracer;
  private currentRootSpan: RootSpan = undefined;
  private isListenersSet = false;
  private config: InteractionConfig;
  private registeredListeners: ListenerRegistration[] = [];
  private lastScrollTime = 0;

  constructor(tracer: Tracer, config: InteractionConfig = {}) {
    this.tracer = tracer;
    const { throttleMs, debounceMs, ...restConfig } = config as InteractionConfig & {
      debounceMs?: number;
    };

    this.config = {
      enabled: false,
      trackClicks: false,
      trackScroll: false,
      trackKeyboard: false, // Off by default for privacy
      trackForm: false,
      trackTouch: false,
      captureElementText: false,
      elementTextMaxLength: 100,
      selectorDepth: 3,
      throttleMs: throttleMs ?? debounceMs ?? 500,
      ...restConfig,
    };
  }

  public subscribe(rootSpan: RootSpan) {
    this.currentRootSpan = rootSpan;

    if (!this.config.enabled) {
      emitInteractionLog("info", "[L9RUM Interactions] Interaction tracking disabled", {
        "l9rum.view.interactions.phase": "subscribe",
      });
      return;
    }

    if (!this.isListenersSet) {
      this.setupEventListeners();
      this.isListenersSet = true;
      emitInteractionLog("info", "[L9RUM Interactions] Event listeners set up", {
        "l9rum.view.interactions.phase": "subscribe",
        "l9rum.view.interactions.config": JSON.stringify(this.config),
      });
    }
  }

  public unsubscribe() {
    this.currentRootSpan = undefined;
    this.cleanup();
  }

  private getParentContext() {
    return this.currentRootSpan ? trace.setSpan(context.active(), this.currentRootSpan) : undefined;
  }

  private setupEventListeners() {
    const listenerDefinitions: ListenerDefinition[] = [
      {
        enabled: this.config.trackClicks,
        type: "click",
        guard: "click_handler",
        handler: (event: Event) => this.recordClickInteraction(event as MouseEvent),
        options: { capture: true, passive: true },
      },
      {
        enabled: this.config.trackScroll,
        type: "scroll",
        guard: "scroll_handler",
        handler: () => this.recordScrollInteraction(),
        options: { capture: true, passive: true },
      },
      {
        enabled: this.config.trackKeyboard,
        type: "keydown",
        guard: "keyboard_handler",
        handler: (event: Event) => this.recordKeyboardInteraction(event as KeyboardEvent),
        options: { capture: true, passive: true },
      },
      {
        enabled: this.config.trackForm,
        type: "input",
        guard: "input_handler",
        handler: (event: Event) => this.recordInputInteraction(event),
        options: { capture: true, passive: true },
      },
      {
        enabled: this.config.trackForm,
        type: "focus",
        guard: "focus_handler",
        handler: (event: Event) => this.recordFocusInteraction(event as FocusEvent),
        options: { capture: true, passive: true },
      },
      {
        enabled: this.config.trackTouch,
        type: "touchstart",
        guard: "touch_handler",
        handler: (event: Event) => this.recordTouchInteraction(event as TouchEvent),
        options: { capture: true, passive: true },
      },
    ];

    listenerDefinitions.forEach((definition) => {
      if (!definition.enabled) return;

      const listener = this.createGuardedListener(definition.guard, definition.handler);
      document.addEventListener(definition.type, listener, definition.options);
      this.registeredListeners.push({
        type: definition.type,
        listener,
        options: definition.options,
      });
    });
  }

  private recordClickInteraction(event: MouseEvent) {
    const parentContext = this.getParentContext();
    if (!parentContext) return;

    const target = event.target as Element;
    if (!target) return;

    const interaction: InteractionEvent = {
      type: "click",
      target: extractElementInfo(target, this.config),
      position: { x: event.clientX, y: event.clientY },
      timestamp: Date.now(),
      metadata: {
        button: event.button,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        metaKey: event.metaKey,
        detail: event.detail, // Click count for double-click detection
        isTrusted: event.isTrusted,
      },
    };

    const spanName = `interaction:click:${interaction.target?.tagName || "unknown"}`;
    const span = this.tracer.startSpan(spanName, { kind: SpanKind.INTERNAL }, parentContext);

    setInteractionAttributes(span, interaction);
    span.end();
  }

  private recordScrollInteraction() {
    const parentContext = this.getParentContext();
    if (!parentContext) return;

    // Throttle scroll events
    const now = Date.now();
    if (now - this.lastScrollTime < (this.config.throttleMs || 500)) {
      return;
    }
    this.lastScrollTime = now;

    const scrollDepth = calculateScrollDepth();
    const interaction: InteractionEvent = {
      type: "scroll",
      timestamp: now,
      metadata: {
        scrollY: window.scrollY,
        scrollX: window.scrollX,
        depthPercent: scrollDepth,
        documentHeight: document.documentElement.scrollHeight,
        viewportHeight: window.innerHeight,
      },
    };

    const span = this.tracer.startSpan(
      "interaction:scroll",
      { kind: SpanKind.INTERNAL },
      parentContext,
    );
    setInteractionAttributes(span, interaction);
    span.end();
  }

  private recordKeyboardInteraction(event: KeyboardEvent) {
    const parentContext = this.getParentContext();
    if (!parentContext) return;

    // Only record non-character keys to avoid capturing user input
    if (!isSafeToTrackKey(event.key)) return;

    const target = event.target as Element;
    const interaction: InteractionEvent = {
      type: "keyboard",
      target: target ? extractElementInfo(target, this.config) : undefined,
      timestamp: Date.now(),
      metadata: {
        key: event.key,
        code: event.code,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        metaKey: event.metaKey,
        repeat: event.repeat,
        location: event.location,
      },
    };

    const span = this.tracer.startSpan(
      "interaction:keyboard",
      { kind: SpanKind.INTERNAL },
      parentContext,
    );
    setInteractionAttributes(span, interaction);
    span.end();
  }

  private recordInputInteraction(event: Event) {
    const parentContext = this.getParentContext();
    if (!parentContext) return;

    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (!target) return;

    // Skip password and sensitive inputs
    if (isSensitiveInput(target)) return;

    const interaction: InteractionEvent = {
      type: "input",
      target: extractElementInfo(target, this.config),
      timestamp: Date.now(),
      metadata: {
        inputType: target.type || "text",
        valueLength: target.value?.length || 0,
        name: target.name || undefined,
        checked:
          target instanceof HTMLInputElement &&
          (target.type === "checkbox" || target.type === "radio")
            ? target.checked
            : undefined,
        required: typeof target.required === "boolean" ? target.required : undefined,
        disabled: typeof target.disabled === "boolean" ? target.disabled : undefined,
        inputMode: target.inputMode || undefined,
        maxLength:
          typeof target.maxLength === "number" && target.maxLength >= 0
            ? target.maxLength
            : undefined,
      },
    };

    const span = this.tracer.startSpan(
      "interaction:input",
      { kind: SpanKind.INTERNAL },
      parentContext,
    );
    setInteractionAttributes(span, interaction);
    span.end();
  }

  private recordFocusInteraction(event: FocusEvent) {
    const parentContext = this.getParentContext();
    if (!parentContext) return;

    const target = event.target as Element;
    if (!target) return;

    const interaction: InteractionEvent = {
      type: "focus",
      target: extractElementInfo(target, this.config),
      timestamp: Date.now(),
    };

    const span = this.tracer.startSpan(
      "interaction:focus",
      { kind: SpanKind.INTERNAL },
      parentContext,
    );
    setInteractionAttributes(span, interaction);
    span.end();
  }

  private recordTouchInteraction(event: TouchEvent) {
    const parentContext = this.getParentContext();
    if (!parentContext) return;

    const target = event.target as Element | null;
    const touch = event.touches[0] || event.changedTouches[0];

    const interaction: InteractionEvent = {
      type: "touch",
      target: target ? extractElementInfo(target, this.config) : undefined,
      position: touch ? { x: touch.clientX, y: touch.clientY } : undefined,
      timestamp: Date.now(),
      metadata: {
        touchCount: event.touches.length,
        isPrimary: touch ? touch.identifier === 0 : undefined,
        radiusX: touch?.radiusX,
        radiusY: touch?.radiusY,
        rotationAngle: touch?.rotationAngle,
        force: touch?.force,
      },
    };

    const span = this.tracer.startSpan(
      "interaction:touch",
      { kind: SpanKind.INTERNAL },
      parentContext,
    );
    setInteractionAttributes(span, interaction);
    span.end();
  }

  private createGuardedListener(guard: string, handler: (event: Event) => void): EventListener {
    return (event: Event) => {
      try {
        handler(event);
      } catch (e) {
        emitInteractionLog(
          "warn",
          "[L9RUM Interactions] Error recording interaction",
          {
            "l9rum.view.interactions.guard": guard,
          },
          e,
        );
      }
    };
  }

  private cleanup() {
    try {
      this.registeredListeners.forEach(({ type, listener, options }) => {
        document.removeEventListener(type, listener, options);
      });
      this.registeredListeners = [];
      this.isListenersSet = false;
      this.lastScrollTime = 0;

      emitInteractionLog("info", "[L9RUM Interactions] Cleaned up event listeners", {
        "l9rum.view.interactions.phase": "cleanup",
      });
    } catch (e) {
      emitInteractionLog(
        "warn",
        "[L9RUM Interactions] Error during cleanup",
        {
          "l9rum.view.interactions.guard": "cleanup",
        },
        e,
      );
    }
  }
}
